import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Typography, Space, Progress, Spin, Alert, Row, Col, Tag, Avatar } from 'antd';
import { 
  PlayCircleOutlined, 
  ArrowLeftOutlined, 
  ClockCircleOutlined, 
  BookOutlined,
  UserOutlined,
  StarFilled,
  CheckCircleFilled,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useSecurityProtection } from '../hooks/useSecurityProtection';
import { useCourseDetails } from '../hooks/course';
import { THEME_COLORS, GRADIENTS, COMMON_STYLES } from '../utils/theme';

const { Title, Text, Paragraph } = Typography;

const CourseDetails: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { data: courseDetails, isLoading, error } = useCourseDetails(undefined, courseId);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Course"
        description="Failed to load course details. Please try again."
        type="error"
        showIcon
        action={
          <Button onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        }
      />
    );
  }

  if (!courseDetails) {
    return (
      <Alert
        message="Course Not Found"
        description="The requested course could not be found."
        type="warning"
        showIcon
        action={
          <Button onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        }
      />
    );
  }

  const handleStartCourse = () => {
    // Navigate to course lessons or learning interface
    navigate(`/course/${courseId}/learn`);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div style={{ 
      maxWidth: COMMON_STYLES.containerMaxWidth,
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px',
      backgroundColor: THEME_COLORS.gray3,
      minHeight: '100vh'
    }}>
      {/* Back Button */}
      <Button 
        icon={<ArrowLeftOutlined />}
        onClick={handleBackToDashboard}
        style={{ marginBottom: '24px' }}
        size="large"
      >
        Back to Dashboard
      </Button>

      <Row gutter={[32, 32]}>
        {/* Left Column - Course Card */}
        <Col xs={24} lg={10}>
          <Card 
            style={{ 
              borderRadius: COMMON_STYLES.cardBorderRadius,
              overflow: 'hidden',
              background: GRADIENTS.primaryBlue,
              color: THEME_COLORS.textLight,
              border: 'none',
              boxShadow: COMMON_STYLES.cardShadow
            }}
          >
            <div style={{ padding: isMobile ? '24px' : '32px', textAlign: 'center' }}>
              <Title 
                level={3} 
                style={{ 
                  color: THEME_COLORS.textLight,
                  marginBottom: '16px',
                  fontSize: isMobile ? '18px' : '22px',
                  fontWeight: 600
                }}
              >
                {courseDetails.title?.toUpperCase() || 'CFA LEVEL I ONLINE COURSE'}
              </Title>
              
              <Paragraph 
                style={{ 
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: isMobile ? '13px' : '14px',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}
              >
                {courseDetails.description || 'The CFA Level I course prepares candidates for the first stage of the Chartered Financial Analyst (CFA) designation, equipping them with globally recognized knowledge and analytical skills essential for careers in finance and investment.'}
              </Paragraph>

              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <BookOutlined style={{ color: THEME_COLORS.accentCyan }} />
                  <Text style={{ color: THEME_COLORS.textLight }}>
                    {courseDetails.lessonsCount || '91'} Lessons
                  </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <ClockCircleOutlined style={{ color: THEME_COLORS.accentCyan }} />
                  <Text style={{ color: THEME_COLORS.textLight }}>
                    {courseDetails.duration_weeks} Complete
                  </Text>
                </div>
              </Space>
            </div>
          </Card>

          {/* Color Palette Display */}
          <Card 
            style={{ 
              borderRadius: COMMON_STYLES.cardBorderRadius,
              marginTop: '20px',
              border: 'none',
              boxShadow: COMMON_STYLES.cardShadow
            }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {[
                { color: THEME_COLORS.primaryDark, label: '182867' },
                { color: THEME_COLORS.primaryBlue, label: '2c479e' },
                { color: THEME_COLORS.accentCyan, label: '44c8f5' },
                { color: THEME_COLORS.gray1, label: 'bfbfbf' },
                { color: THEME_COLORS.gray2, label: 'f0f0f0' },
                { color: THEME_COLORS.gray3, label: 'f5f5f5' },
              ].map((item, index) => (
                <div 
                  key={index}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px'
                  }}
                >
                  <div 
                    style={{ 
                      width: '40px', 
                      height: '40px', 
                      backgroundColor: item.color,
                      borderRadius: '8px',
                      border: `1px solid ${THEME_COLORS.gray2}`
                    }} 
                  />
                  <Text style={{ fontFamily: 'monospace' }}>{item.label}</Text>
                </div>
              ))}
            </Space>
          </Card>
        </Col>

        {/* Right Column - Course Details and Lessons */}
        <Col xs={24} lg={14}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Section Title Card */}
            <Card 
              style={{ 
                borderRadius: COMMON_STYLES.cardBorderRadius,
                backgroundColor: THEME_COLORS.primaryBlue,
                border: 'none',
                boxShadow: COMMON_STYLES.cardShadow,
                textAlign: 'center'
              }}
            >
              <Title 
                level={4} 
                style={{ 
                  color: THEME_COLORS.textLight,
                  margin: 0,
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}
              >
                QUANTITATIVE METHODS
              </Title>
            </Card>

            {/* Lessons List */}
            <Card 
              style={{ 
                borderRadius: COMMON_STYLES.cardBorderRadius,
                border: 'none',
                boxShadow: COMMON_STYLES.cardShadow
              }}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {[
                  { id: 'LM01', title: 'Rates and Returns', completed: true },
                  { id: 'LM03', title: 'Statistical Measures of Asset Returns', completed: false },
                  { id: 'LM04', title: 'Probability Trees and Conditional Expectations', completed: true },
                  { id: 'LM05', title: 'Portfolio Mathematics', completed: true },
                  { id: 'LM06', title: 'Simulation Methods', completed: true },
                  { id: 'LM07', title: 'Estimation and Inference', completed: false },
                  { id: 'LM08', title: 'Hypothesis Testing', completed: false },
                  { id: 'LM09', title: 'Parametric and Non-Parametric Tests of Independence', completed: false },
                  { id: 'LM10', title: 'Simple Linear Regression', completed: false },
                ].map((lesson, index) => (
                  <div 
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      borderRadius: '12px',
                      backgroundColor: THEME_COLORS.gray3,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => {
                      // Navigate to lesson
                      console.log(`Navigate to ${lesson.id}`);
                    }}
                  >
                    {lesson.completed ? (
                      <CheckCircleFilled 
                        style={{ 
                          fontSize: '28px',
                          color: THEME_COLORS.primaryBlue,
                          flexShrink: 0
                        }} 
                      />
                    ) : (
                      <CheckCircleOutlined 
                        style={{ 
                          fontSize: '28px',
                          color: THEME_COLORS.gray1,
                          flexShrink: 0
                        }} 
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <Text 
                        strong 
                        style={{ 
                          fontSize: isMobile ? '14px' : '16px',
                          color: THEME_COLORS.textPrimary
                        }}
                      >
                        {lesson.id}: {lesson.title}
                      </Text>
                    </div>
                  </div>
                ))}
              </Space>
            </Card>

            {/* Progress (if enrolled) */}
            {courseDetails.progress !== undefined && courseDetails.progress > 0 && (
              <Card 
                style={{ 
                  borderRadius: COMMON_STYLES.cardBorderRadius,
                  border: 'none',
                  boxShadow: COMMON_STYLES.cardShadow
                }}
              >
                <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                  Your Progress
                </Text>
                <Progress 
                  percent={courseDetails.progress} 
                  strokeColor={THEME_COLORS.primaryBlue}
                />
                <Text type="secondary">
                  {courseDetails.progress}% Complete
                </Text>
              </Card>
            )}

            {/* Action Buttons */}
            <Space size="middle" style={{ width: '100%' }}>
              <Button 
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={handleStartCourse}
                style={{
                  height: '52px',
                  borderRadius: COMMON_STYLES.buttonBorderRadius,
                  fontSize: '16px',
                  fontWeight: '600',
                  flex: 1,
                  backgroundColor: THEME_COLORS.primaryBlue,
                  borderColor: THEME_COLORS.primaryBlue
                }}
              >
                {courseDetails.progress > 0 ? 'Continue Learning' : 'Start Course'}
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default CourseDetails;
